import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import styled from 'styled-components';
import Icon from '../Icon';
import { GridTableCell, GridTableHeaderCell } from './GridTableCell';

type ColorProps = {
  backgroundColor: string;
  fontColor: string;
};

type GridTableProps<TableContent, RowProps> = {
  tableData?: TableContent[];
  isLastViewed?: boolean;
  sortOptions?: { column: string; direction: 'asc' | 'desc' };
  columnOptions: {
    type: 'boolean' | 'number' | 'string';
    name: string;
    label: string | JSX.Element;
    isSortable?: boolean;
    width?: string | null;
    textAlign?: 'center' | 'left' | 'right' | null;
  }[];
  Row: ({
    content,
    tableRowCustomProps,
  }: {
    content: TableContent;
    tableRowCustomProps?: RowProps | {};
  }) => JSX.Element;
  setSortOption?: React.Dispatch<
    React.SetStateAction<{ column: string; direction: 'asc' | 'desc' }>
  >;
  tableRowCustomProps?: RowProps | {};
  isLoading?: boolean;
  theme?: 'lightYellow' | 'default';
};

const GridTable = <TableContent, RowProps>({
  tableData,
  columnOptions,
  sortOptions,
  setSortOption,
  Row,
  tableRowCustomProps = {},
  isLoading,
  theme = 'default',
}: GridTableProps<TableContent, RowProps>) => {
  const colors = {
    default: {
      backgroundColor: '#fff',
      fontColor: '##000',
    },
    lightYellow: {
      backgroundColor: '#fff3cd',
      fontColor: '#856404',
    },
  };

  const isEmpty = (val: any) =>
    val == null || val === '' || val === '-';

  const sortData = (a: any, b: any) => {
    if (!sortOptions) return 1;

    const col = sortOptions.column;
    const asc = sortOptions.direction === 'asc';
    const colType = columnOptions.find(option => option.name === col)?.type;

    // Push empty/'-' values to the end regardless of direction
    const aEmpty = isEmpty(a[col]);
    const bEmpty = isEmpty(b[col]);
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;

    let primary = 0;

    switch (colType) {
      case 'boolean':
        if (a[col] !== b[col]) {
          primary = (b[col] ? 1 : -1) * (asc ? 1 : -1);
        }
        break;
      case 'number':
        if ((a[col] ?? 0) !== (b[col] ?? 0)) {
          primary = ((a[col] ?? 0) > (b[col] ?? 0) ? 1 : -1) * (asc ? 1 : -1);
        }
        break;
      default: {
        const aVal = a[col]?.toLowerCase() ?? '';
        const bVal = b[col]?.toLowerCase() ?? '';
        if (aVal !== bVal) {
          primary = (aVal > bVal ? 1 : -1) * (asc ? 1 : -1);
        }
        break;
      }
    }

    if (primary !== 0) return primary;

    // Secondary sort: priority ascending (lower = more urgent)
    if ('priority' in a && 'priority' in b) {
      const prioCmp = (a.priority ?? 0) - (b.priority ?? 0);
      if (prioCmp !== 0) return prioCmp;
    }

    // Tertiary sort: claimcaseKey ascending for deterministic order
    if ('claimcaseKey' in a && 'claimcaseKey' in b) {
      const aKey = a.claimcaseKey?.toLowerCase() ?? '';
      const bKey = b.claimcaseKey?.toLowerCase() ?? '';
      if (aKey !== bKey) return aKey > bKey ? 1 : -1;
    }

    return 0;
  };

  const handleSortClick = (column: string, isSortable: boolean) => () => {
    if (!isSortable) return;
    if (setSortOption) {
      column === sortOptions?.column
        ? setSortOption(
            (state: { column: string; direction: 'asc' | 'desc' }) => ({
              ...state,
              direction: sortOptions.direction === 'asc' ? 'desc' : 'asc',
            })
          )
        : setSortOption(
            (state: { column: string; direction: 'asc' | 'desc' }) => ({
              ...state,
              column,
            })
          );
    }
  };

  const NO_OF_LOADING_ROWS = 15;

  return (
    <>
      <Table
        columnsWidth={columnOptions
          .map(option => option.width || 'auto')
          .join(' ')}
        numberOfColumns={columnOptions.length}
        themeColors={colors[theme]}
      >
        {columnOptions.map((column, index) => (
          <GridTableHeaderCell
            key={index}
            textAlign={column.textAlign || 'left'}
            onClick={handleSortClick(column.name, column.isSortable ?? false)}
            isSortable={column.isSortable ?? false}
            theme={theme}
          >
            {column.label}
            {column.name === sortOptions?.column ? (
              sortOptions.direction === 'asc' ? (
                <>
                  {' '}
                  <Icon iconName='faSortUp' />
                </>
              ) : (
                <>
                  {' '}
                  <Icon iconName='faSortDown' />
                </>
              )
            ) : column.isSortable ? (
              <FadedIconWrapper>
                {' '}
                <Icon iconName='faSort' />
              </FadedIconWrapper>
            ) : null}
          </GridTableHeaderCell>
        ))}
        {isLoading
          ? [...Array(columnOptions.length * NO_OF_LOADING_ROWS)].map(
              (x, i) => (
                <GridTableCell key={i}>
                  <Skeleton />
                </GridTableCell>
              )
            )
          : tableData
              ?.sort(sortData)
              .map((content, index) => (
                <Row key={index} content={content} {...tableRowCustomProps} />
              ))}
      </Table>
    </>
  );
};

export default GridTable;

const Table = styled.div<{
  columnsWidth: string;
  numberOfColumns: number;
  themeColors: ColorProps;
}>`
  display: grid;
  grid-template-columns: ${({ columnsWidth }) => columnsWidth};
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
  color: ${({ themeColors }) => themeColors.fontColor};
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.875rem;
`;

const FadedIconWrapper = styled.span`
  color: #dedcdc;
`;
